import React from 'react';
import styled from 'styled-components';
import { ReactComponent as VerifiedIcon } from '../../assets/verified.svg';
import VipIcon from '../Icons/VipIcons';
import { usePresence } from '../../contexts/PresenceProvider';

const AvatarContainer = styled.div`
  width: ${props => props.size || '40px'};
  height: ${props => props.size || '40px'};
  border-radius: ${props => props.$rounded ? '8px' : '50%'};
  overflow: visible; /* Visible to prevent clipping of indicators */
  background: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: ${props => props.onClick ? 'pointer' : 'default'};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  container-type: inline-size; /* Enable cqw units for responsive initials */
  
  ${props => props.onClick && `
    &:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
    
    &:active {
      transform: scale(0.95);
    }
  `}
  
  ${props => props.$border && `
    border: 2px solid ${props.theme.colors.bg};
    box-shadow: 0 0 0 1px ${props.theme.colors.border};
  `}
`;

const AvatarImage = styled.img.attrs(props => ({
  fetchPriority: props.$fetchPriority // React uses camelCase for DOM attributes
}))`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: inherit; /* Inherit border radius from container */
  /* Optimize for faster rendering */
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
`;

const AvatarInitials = styled.span`
  color: white;
  font-weight: 600;
  /* Fallback for browsers without container query support */
  font-size: ${props => {
    const sizeStr = String(props.size || '40px');
    if (sizeStr.endsWith('%')) return '24px'; /* reasonable default for % containers */
    const size = parseInt(sizeStr) || 40;
    if (size >= 80) return `${Math.round(size * 0.4)}px`;
    if (size >= 60) return `${Math.round(size * 0.35)}px`;
    if (size >= 40) return `${Math.round(size * 0.4)}px`;
    return `${Math.round(size * 0.45)}px`;
  }};
  /* Modern browsers: scale with actual container size via container query units */
  font-size: 40cqw;
  text-transform: uppercase;
  user-select: none;
`;

const OnlineIndicator = styled.div`
  width: ${props => {
    const size = props.size;
    // Handle percentage or non-numeric sizes - use relative positioning
    if (typeof size === 'string' && (size.includes('%') || isNaN(parseInt(size)))) {
      return '20%';
    }
    const sizeNum = parseInt(size) || 40;
    return `${Math.max(12, sizeNum * 0.2)}px`; /* Smaller indicator */
  }};
  height: ${props => {
    const size = props.size;
    // Handle percentage or non-numeric sizes - use relative positioning
    if (typeof size === 'string' && (size.includes('%') || isNaN(parseInt(size)))) {
      return '20%';
    }
    const sizeNum = parseInt(size) || 40;
    return `${Math.max(12, sizeNum * 0.2)}px`; /* Smaller indicator */
  }};
  min-width: 12px;
  min-height: 12px;
  max-width: 24px;
  max-height: 24px;
  position: absolute;
  bottom: ${props => {
    const size = props.size;
    // Handle percentage or non-numeric sizes
    if (typeof size === 'string' && (size.includes('%') || isNaN(parseInt(size)))) {
      return '2%';
    }
    const sizeNum = parseInt(size) || 40;
    return sizeNum > 60 ? '4px' : '2px'; /* Position slightly inside avatar */
  }};
  ${props => props.$indicatorPosition === 'left' ? `
    left: ${(() => {
      const size = props.size;
      // Handle percentage or non-numeric sizes
      if (typeof size === 'string' && (size.includes('%') || isNaN(parseInt(size)))) {
        return '2%';
      }
      const sizeNum = parseInt(size) || 40;
      return sizeNum > 60 ? '4px' : '2px';
    })()};
  ` : `
    right: ${(() => {
      const size = props.size;
      // Handle percentage or non-numeric sizes
      if (typeof size === 'string' && (size.includes('%') || isNaN(parseInt(size)))) {
        return '2%';
      }
      const sizeNum = parseInt(size) || 40;
      return sizeNum > 60 ? '4px' : '2px';
    })()};
  `}
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 50%;
  padding: 1px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2); /* Subtle shadow */
`;

const AvatarBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  background: ${props => props.color || props.theme.colors.error};
  color: white;
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  border: 2px solid ${props => props.theme.colors.bg};
  z-index: 11; /* Higher than online indicator */
`;

const VerifiedBadge = styled.div`
  position: absolute;
  bottom: ${props => {
    const size = parseInt(props.size) || 40;
    const indicatorSize = Math.max(16, size * 0.3);
    return `-${indicatorSize * 0.1}px`; /* Slightly offset from bottom */
  }};
  right: ${props => {
    const size = parseInt(props.size) || 40;
    const indicatorSize = Math.max(16, size * 0.3);
    return `${indicatorSize + 2}px`; /* Position to left of online indicator */
  }};
  width: ${props => {
    const size = parseInt(props.size) || 40;
    return `${Math.max(size * 0.25, 18)}px`;
  }};
  height: ${props => {
    const size = parseInt(props.size) || 40;
    return `${Math.max(size * 0.25, 18)}px`;
  }};
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  z-index: 9; /* Lower than online indicator but higher than avatar */
  
  svg {
    width: 80%;
    height: 80%;
  }
`;

const getInitials = (name) => {
  if (!name) return '?';
  const words = name.trim().split(' ');
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return '#9e9e9e';
  
  const colors = [
    '#f44336', '#e91e63', '#9c27b0', '#673ab7',
    '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4',
    '#009688', '#4caf50', '#8bc34a', '#cddc39',
    '#ffeb3b', '#ffc107', '#ff9800', '#ff5722'
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

/**
 * Avatar Component
 * 
 * Displays user profile pictures with automatic online/offline indicators.
 * Online indicators are shown by default for consistency across the app.
 * Real-time presence detection is automatic when userId is provided.
 * 
 * @param {string} src - Profile picture URL
 * @param {string} name - User's name (used for initials fallback)
 * @param {string} size - Avatar size (default: '40px')
 * @param {boolean} showOnline - Show online indicator (default: true)
 * @param {boolean} isOnline - Explicit online status (overrides presence detection)
 * @param {string|number} userId - User ID for real-time presence detection
 * @param {boolean} hideOnlineIndicator - Force hide online indicator for specific use cases
 * @param {string} indicatorPosition - Position of online indicator: 'left' or 'right' (default: 'right')
 * @param {boolean} isVerified - Show verified badge
 * @param {boolean} border - Add border around avatar
 * @param {boolean} rounded - Use rounded corners instead of circle
 * @param {string|number} badge - Show notification badge
 * @param {string} badgeColor - Badge background color
 * @param {string} alt - Alt text for image
 * @param {string} fallbackSrc - Fallback image URL
 * @param {string} className - Additional CSS classes
 * @param {object} style - Inline styles
 * @param {function} onClick - Click handler
 * 
 * Examples:
 * 
 * // Basic usage with indicator on right (default)
 * <Avatar src={user.avatar} name={user.name} userId={user.id} />
 * 
 * // Position indicator on the left
 * <Avatar src={user.avatar} name={user.name} userId={user.id} indicatorPosition="left" />
 * 
 * // Hide online indicator for non-user contexts
 * <Avatar src={brand.logo} name={brand.name} hideOnlineIndicator={true} />
 * 
 * // Override with explicit online status
 * <Avatar src={user.avatar} name={user.name} userId={user.id} isOnline={false} />
 */

export const Avatar = ({ 
  src, 
  name, 
  size = '40px', 
  showOnline = true, // Show online indicator by default for consistency
  isOnline, // Controls online/offline state - if not provided, will use presence logic
  userId, // User ID for presence detection
  hideOnlineIndicator = false, // New prop to explicitly hide online indicator when needed
  indicatorPosition = 'right', // Position of online indicator: 'left' or 'right'
  onlineColor,
  onClick,
  border = false,
  rounded = false,
  badge,
  badgeColor,
  alt,
  fallbackSrc,
  className,
  style,
  isVerified = false,
  loading = 'lazy', // Lazy load images by default
  fetchPriority = 'auto', // Priority hint for critical images
  decoding = 'async' // Image decoding strategy: 'async', 'sync', or 'auto'
}) => {
  const [imageError, setImageError] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [currentSrc, setCurrentSrc] = React.useState(src);
  const [imageKey, setImageKey] = React.useState(0);
  
  // Use presence system
  const { trackUser, userPresenceMap } = usePresence();
  
  // Track user presence when userId changes
  React.useEffect(() => {
    if (userId && !hideOnlineIndicator) {
      trackUser(userId);
    }
  }, [userId, hideOnlineIndicator, trackUser]);
  
  // Memoize the online status to prevent excessive calls
  const actualIsOnline = React.useMemo(() => {
    // 1. If isOnline is explicitly provided as a boolean, use it
    if (typeof isOnline === 'boolean') {
      return isOnline;
    }
    
    // 2. If userId is provided, check global presence
    if (userId && userPresenceMap) {
      const presence = userPresenceMap.get(userId);
      return presence?.status === 'online' || presence?.status === 'away' || presence?.status === 'busy';
    }
    
    // 3. Default to false (offline) if no presence data
    return false;
  }, [isOnline, userId, userPresenceMap]);
  
  // Reset error state when src changes and force re-render
  React.useEffect(() => {
    if (src !== currentSrc) {
      setCurrentSrc(src);
      setImageError(false);
      setImageLoaded(false);
      // Force immediate re-render by updating key
      setImageKey(prev => prev + 1);
    }
  }, [src, currentSrc]);
  
  const handleImageError = () => {
    setImageError(true);
    if (fallbackSrc && src !== fallbackSrc) {
      setImageError(false);
      // Try fallback source
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Show image if we have a src and no error occurred
  const shouldShowImage = src && !imageError;
  const initials = getInitials(name || alt);
  const backgroundColor = getAvatarColor(name || alt || '');

  return (
    <AvatarContainer 
      size={size} 
      onClick={onClick}
      $border={border}
      $rounded={rounded}
      className={className}
      style={{ backgroundColor, ...style }}
    >
      {shouldShowImage ? (
        <AvatarImage 
          key={imageKey}
          src={imageError && fallbackSrc ? fallbackSrc : src}
          alt={alt || name || 'Avatar'}
          onError={handleImageError}
          onLoad={handleImageLoad}
          loading={loading}
          $fetchPriority={fetchPriority}
          decoding={decoding}
          style={{ 
            opacity: imageLoaded ? 1 : 0.8,
            // Faster transition for critical images, or none for sync decoding
            transition: decoding === 'sync' ? 'none' : 'opacity 0.15s ease'
          }}
        />
      ) : (
        <AvatarInitials size={size}>
          {initials}
        </AvatarInitials>
      )}
      
      {showOnline && !hideOnlineIndicator && (
        <OnlineIndicator size={size} $indicatorPosition={indicatorPosition}>
          <VipIcon 
            name={actualIsOnline ? "OnlineIndicator" : "OfflineIndicator"}
            size="100%"
            style={{ 
              color: actualIsOnline ? '#22c55e' : '#94a3b8' /* Green for online, gray for offline */
            }}
          />
        </OnlineIndicator>
      )}
      
      {badge && (
        <AvatarBadge color={badgeColor}>
          {badge}
        </AvatarBadge>
      )}
      
      {isVerified && (
        <VerifiedBadge size={size}>
          <VerifiedIcon />
        </VerifiedBadge>
      )}
    </AvatarContainer>
  );
};

// Avatar group component for showing multiple avatars
const AvatarGroupContainer = styled.div`
  display: flex;
  align-items: center;
  
  ${AvatarContainer} {
    margin-left: ${props => `-${parseInt(props.spacing) || 8}px`};
    border: 2px solid ${props => props.theme.colors.bg};
    z-index: ${props => props.children?.length || 0};
    
    &:first-child {
      margin-left: 0;
    }
    
    &:hover {
      z-index: 999;
    }
  }
`;

const MoreAvatars = styled(AvatarContainer)`
  background: ${props => props.theme.colors.bg2};
  color: ${props => props.theme.colors.text};
  font-weight: 600;
  font-size: ${props => {
    const size = parseInt(props.size) || 40;
    return `${Math.round(size * 0.35)}px`;
  }};
`;

export const AvatarGroup = ({ 
  avatars = [], 
  size = '40px', 
  max = 3, 
  spacing = 8,
  showMore = true 
}) => {
  const visibleAvatars = avatars.slice(0, max);
  const remainingCount = avatars.length - max;

  return (
    <AvatarGroupContainer spacing={spacing}>
      {visibleAvatars.map((avatar, index) => (
        <Avatar
          key={avatar.id || index}
          src={avatar.src}
          name={avatar.name}
          size={size}
          style={{ zIndex: visibleAvatars.length - index }}
        />
      ))}
      
      {showMore && remainingCount > 0 && (
        <MoreAvatars size={size}>
          +{remainingCount}
        </MoreAvatars>
      )}
    </AvatarGroupContainer>
  );
};
